import { gql } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Link,
  StatusBadge,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import { sortBy } from 'lodash';
import React from 'react';

import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';
import { DocsRouter } from 'src/lib/routers';

import { channelTypeLogo } from '../helpers/helpers';

import { ChannelActionMenu } from './channelActionMenu/ChannelActionMenu';
import { NotificationActionMenu } from './notificationActionMenu/NotificationActionMenu';

export const ChannelCardChannelFragment = gql`
  fragment ChannelCardChannelFragment on Channel {
    id
    name
    subscriptions {
      id
      enabled
      variant
      __typename
      ... on ProposalLifecycleSubscription {
        events
      }
    }
    ... on WebhookChannel {
      id
      proposalLifecycleSubscriptions {
        id
        enabled
        events
        variant
        __typename
      }
    }
    __typename
  }
`;

const subscriptionTypeDisplayName = {
  ScheduledSummary: 'Daily Report',
  RegistrySubscription: 'Schema Change',
  SchemaPublishSubscription: 'Schema Change',
  CompositionStatusSubscription: 'Build Status',
  QueryTrigger: 'Performance Alert',
  ProposalLifecycleSubscription: 'Proposal Lifecycle',
};

const proposalEventTypeDisplayName = {
  PROPOSAL_CREATED: 'APOLLO_PROPOSAL_CREATED',
  REVISION_SAVED: 'APOLLO_PROPOSAL_REVISION_SAVED',
  STATUS_CHANGE: 'APOLLO_PROPOSAL_STATUS_CHANGE',
};
interface Props {
  graphId: string;
  channel: GraphQLTypes.ChannelCardChannelFragment;
  className?: string;
  onEditChannelClick: (channelId: string) => void;
}
export function ChannelCard({
  className,
  graphId,
  channel,
  onEditChannelClick,
}: Props) {
  const showProposalWebhooks = useLDFlag('nebula_proposals_webhooks_fe');

  const proposalLifecycleSubscriptions =
    channel.__typename === 'WebhookChannel'
      ? channel.proposalLifecycleSubscriptions
      : [];
  const subscriptions = [
    ...channel.subscriptions.filter(
      (sub) => sub.__typename !== 'ProposalLifecycleSubscription',
    ),
    ...(showProposalWebhooks ? proposalLifecycleSubscriptions : []),
  ];
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>
          <img
            alt="channel logo"
            className="size-8"
            src={channelTypeLogo[channel.__typename]}
          />
          {channel.name}
        </CardTitle>

        <PermissionGuard
          graphId={graphId}
          graphPermissions="canQueryPrivateInfo"
          fallback={null}
        >
          <PermissionWarning
            graphId={graphId}
            graphPermissions={['canManageIntegrations']}
            childRenderer={(hasPermission) => (
              <ButtonGroup>
                <Button
                  variant="secondary"
                  isDisabled={!hasPermission}
                  onClick={() => onEditChannelClick(channel.id)}
                >
                  Edit
                </Button>
                <ChannelActionMenu
                  disabled={!hasPermission}
                  graphId={graphId}
                  channel={channel}
                />
              </ButtonGroup>
            )}
          />
        </PermissionGuard>
      </CardHeader>
      <CardBody>
        {subscriptions.length === 0 ? (
          <p className="text-secondary">no notifications yet</p>
        ) : (
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>Notification type</Th>
                  <Th className="w-1/4">Variant</Th>
                  <Th className="w-1/4">State</Th>
                  <Th className="w-[5%]">
                    <span className="sr-only">Actions</span>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortBy(
                  subscriptions,
                  (sub) => subscriptionTypeDisplayName[sub.__typename],
                ).map((sub) => {
                  return (
                    <Tr key={sub.id}>
                      <Td>
                        {subscriptionTypeDisplayName[sub.__typename]}
                        <ul>
                          {sub.__typename === 'ProposalLifecycleSubscription' &&
                            sub.events.map((event) => (
                              <li className="mt-1" key={event}>
                                <Link
                                  isExternal
                                  to={DocsRouter.path(
                                    'SchemaProposalNotificationsFormat',
                                  )}
                                >
                                  {proposalEventTypeDisplayName[event]}
                                </Link>
                              </li>
                            ))}
                        </ul>
                      </Td>
                      <Td>
                        {sub.__typename === 'ProposalLifecycleSubscription' ||
                        !sub.variant ? (
                          <span className="italic">Any</span>
                        ) : (
                          sub.variant
                        )}
                      </Td>
                      <Td>
                        {sub.enabled ? (
                          <StatusBadge variant="hidden" colorScheme="blue">
                            Enabled
                          </StatusBadge>
                        ) : (
                          <StatusBadge variant="hidden" colorScheme="gray">
                            Disabled
                          </StatusBadge>
                        )}
                      </Td>
                      <Td isActionsMenu>
                        <PermissionGuard
                          graphId={graphId}
                          graphPermissions="canQueryPrivateInfo"
                          fallback={null}
                        >
                          <PermissionWarning
                            graphId={graphId}
                            graphPermissions={['canManageIntegrations']}
                            childRenderer={(hasPermission) => (
                              <NotificationActionMenu
                                disabled={!hasPermission}
                                graphId={graphId}
                                subscription={sub}
                                channel={channel}
                              />
                            )}
                          />
                        </PermissionGuard>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </CardBody>
    </Card>
  );
}
